// Utility functions

import { isMobile } from 'APP/Helpers/checkPlatform'
import Config from 'APP/Config'
import I18n from 'APP/I18n'

export const isValidEmailAddress = (address: string): boolean => {
  const EMAIL_REGEXP =
    /^[a-z0-9!#$%&'*+ /=?^_`{|}~.-]+@[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/i

  return !!(address && address.length > 5 && EMAIL_REGEXP.test(address))
}

export const hasLoginHash = (hash: string): boolean => {
  const loginHashFields = ['access_token', 'scope', 'expires_in', 'token_type', 'state']
  const hashFields = hash.split('&').map((piece) => piece.split('=')[0].replace('#', ''))
  return !!loginHashFields.every((field) => hashFields.includes(field))
}

export const MINUTE_IN_MILLISECOND = 60000

export const clearAllQueryParamsAndHash = (): void => {
  if (isMobile()) return
  const url = new URL(window.location.origin + window.location.pathname)
  window.history.replaceState(null, '', url)
}

// Returns the oldest timestamp allowed for a public session
export const getMaxPublicSessionTimestamp = (): number => {
  return Date.now() - Config.PUBLIC_COMPUTER_MINUTE_SESSION_LENGTH * MINUTE_IN_MILLISECOND
}

// show the modal after the session length - 1 min (2h59)
export const INACTIVITY_TIMEOUT: number =
  Config.PUBLIC_COMPUTER_MINUTE_SESSION_LENGTH * MINUTE_IN_MILLISECOND - 60 * 1000

// Needed on web, since Auth0 login is a full-page
// redirect which wipes in-memory Redux state
const DEFERRED_DEEPLINK_SESSION_KEY = 'deferredDeeplink'

type StashedDeeplink = { path: string | null; props: Record<string, unknown> }

export const stashDeferredDeeplink = (deeplink?: StashedDeeplink | null): void => {
  if (isMobile()) return
  if (!deeplink) return
  // action/login deeplinks drive the login flow itself; restoring one post-login
  // would re-trigger the log-out/log-in dance, so we don't stash them

  if (!deeplink.path && !deeplink.props?.type) return
  const isLoginActionDeeplink = !!deeplink.path?.startsWith('action/login')
  if (isLoginActionDeeplink) return

  // the deeplink has a path and it's not action/login so we save it for after
  try {
    window.sessionStorage.setItem(
      DEFERRED_DEEPLINK_SESSION_KEY,
      JSON.stringify({ path: deeplink.path, props: deeplink.props })
    )
  } catch {
    // sessionStorage can throw (private mode, quota); losing the stash is non-fatal.
  }
}

export const popDeferredDeeplink = (): StashedDeeplink | null => {
  if (isMobile()) return null
  try {
    const defferedDeeplinkSession = window.sessionStorage.getItem(DEFERRED_DEEPLINK_SESSION_KEY)
    if (!defferedDeeplinkSession) return null
    window.sessionStorage.removeItem(DEFERRED_DEEPLINK_SESSION_KEY)
    return JSON.parse(defferedDeeplinkSession) as StashedDeeplink
  } catch {
    return null
  }
}

// Ensure to return a proper url, where query params are appended properly
// Query params are located at the end of the URL, where it starts with a ‘?’, and multiple params are then separated by an ‘&’
// ex: https://dialogue.co/path?name=Branch&products=[phone,tablet]
export const getHref = (): string | undefined => {
  if (isMobile()) return undefined
  const url = new URL(window.location.href)
  url.searchParams.set('lng', I18n.locale)
  return url.href
}
